import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import logo from "../../assets/images/logo.png";
import { useLocation } from "@reach/router";

function Seo({ description, lang, meta, title, keyword, breadcrumbList, schemaList, image }) {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
        }
      }
    }
  `);

  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (image !== undefined) {
      const img = new Image();
      img.src = image;

      img.onload = () => {
        setImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
    }
  }, [image]);

  const metaDescription = description || site.siteMetadata.description;
  const location = useLocation();
  const currentUrl = location.href;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "InfyOm Technologies",
    alternateName: "InfyOm",
    url: "https://infyom.com",
    logo: `https://infyom.com/${logo}`,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+91 7096336561",
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: ["en", "Hindi", "Gujarati"],
    },
    sameAs: [
      "https://www.facebook.com/infyom",
      "https://twitter.com/infyom",
      "https://www.instagram.com/infyomtechnologies/",
      "https://www.youtube.com/InfyOmTechnologies",
      "https://in.linkedin.com/company/infyom-technologies",
      "https://github.com/InfyOmLabs",
    ],
  };

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      description={description}
      keyword={keyword}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:url`,
          content: currentUrl,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:image`,
          content: image,
        },
        {
          property: `og:locale`,
          content: `en_US`,
        },
        {
          property: `og:site_name`,
          content: `InfyOm Technologies`,
        },
        {
          property: `og:image:width`,
          content: imageDimensions.width,
        },
        {
          property: `og:image:height`,
          content: imageDimensions.height,
        },
        {
          name: `og:image:type`,
          content: `image/png`,
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:site`,
          content: `@infyom`,
        },
        {
          name: `twitter:creator`,
          content: `@infyom`,
        },
        {
          name: `twitter:image`,
          content: image,
        },
        {
          name: `twitter:label1`,
          content: `Written by`,
        },
        {
          name: `twitter:data1`,
          content: `InfyOm Technologies`,
        },
        {
          name: `twitter:label2`,
          content: `Reading time`,
        },
        {
          name: `twitter:data2`,
          content: `Less than a minute`,
        },
        {
          name: `google-site-verification`,
          content: "BNBrVYy7PE9WgoyLCoMzp_brVpo-cpG_UWlKDI4pVRo",
        },
      ].concat(meta)}
    >
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
      {breadcrumbList && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbList)}</script>
      )}
      {schemaList && <script type="application/ld+json">{JSON.stringify(schemaList)}</script>}
    </Helmet>
  );
}

Seo.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
};

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  keyword: PropTypes.string,
};

export default Seo;
